import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Edit2,
  Trash2,
  Shield,
  Star,
  Settings,
  Hammer,
  Clock,
  Copy,
  Briefcase,
  Globe,
} from 'lucide-react'
import type { ArtefactTemplate } from '../../types/artefact-templates'
import { ArtefactTagList } from './ArtefactTagList'

interface OrgTemplateCardProps {
  template: ArtefactTemplate
  isSystem?: boolean | undefined
  isCaseTemplate?: boolean | undefined
  deleteConfirm?: boolean | undefined
  onEdit?: (() => void) | undefined
  onDelete?: (() => void) | undefined
  onDeleteConfirm?: (() => void) | undefined
  onDeleteCancel?: (() => void) | undefined
  onTestBuild?: (() => void) | undefined
  onCollectorSettings?: (() => void) | undefined
  onSaveAsOrg?: (() => void) | undefined
}

export function OrgTemplateCard({
  template,
  isSystem,
  isCaseTemplate,
  deleteConfirm,
  onEdit,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
  onTestBuild,
  onCollectorSettings,
  onSaveAsOrg,
}: OrgTemplateCardProps): ReactElement {
  const isReadOnly = Boolean(isSystem) || Boolean(isCaseTemplate)
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start justify-between">
        <CardInfo template={template} isSystem={isSystem} isCaseTemplate={isCaseTemplate} />
        <CardActionsBar
          isReadOnly={isReadOnly}
          onTestBuild={onTestBuild}
          onSaveAsOrg={onSaveAsOrg}
          template={template}
          deleteConfirm={deleteConfirm}
          onEdit={onEdit}
          onDelete={onDelete}
          onDeleteConfirm={onDeleteConfirm}
          onDeleteCancel={onDeleteCancel}
          onCollectorSettings={onCollectorSettings}
        />
      </div>
    </div>
  )
}

function CardActionsBar({
  isReadOnly,
  onTestBuild,
  onSaveAsOrg,
  template,
  deleteConfirm,
  onEdit,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
  onCollectorSettings,
}: OrgTemplateCardProps & { isReadOnly: boolean }): ReactElement {
  if (isReadOnly) {
    return <ReadOnlyActions onTestBuild={onTestBuild} onSaveAsOrg={onSaveAsOrg} />
  }
  if (deleteConfirm) {
    return (
      <DeleteConfirmBar
        templateName={template.name}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />
    )
  }
  return (
    <OrgActionButtons
      onTestBuild={onTestBuild}
      onCollectorSettings={onCollectorSettings}
      onEdit={onEdit}
      onDelete={onDelete}
    />
  )
}

function CardInfo({
  template,
  isSystem,
  isCaseTemplate,
}: {
  template: ArtefactTemplate
  isSystem?: boolean | undefined
  isCaseTemplate?: boolean | undefined
}): ReactElement {
  const hasSettings = template.collectorSettings !== null

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="font-heading truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
          {template.name}
        </h5>
        <ScopeBadges
          template={template}
          isSystem={isSystem}
          isCaseTemplate={isCaseTemplate}
          hasSettings={hasSettings}
        />
      </div>
      {template.description && (
        <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
          {template.description}
        </p>
      )}
      <ArtefactTagList artefacts={template.artefacts} templateName={template.name} />
      <CardMeta template={template} />
    </div>
  )
}

function ScopeBadges({
  template,
  isSystem,
  isCaseTemplate,
  hasSettings,
}: {
  template: ArtefactTemplate
  isSystem?: boolean | undefined
  isCaseTemplate?: boolean | undefined
  hasSettings: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <>
      {template.isDefault && (
        <span className="dark:bg-[#8a9bba]/15/50 inline-flex items-center gap-1 rounded-full bg-[#4e5e82]/10 px-2 py-0.5 text-xs font-medium text-[#5c6e94] dark:text-[#8a9bba]">
          <Star className="h-3 w-3" />
          {t('collections.artefactTemplates.defaultBadge')}
        </span>
      )}
      {isSystem && (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          <Shield className="h-3 w-3" />
          {t('collections.artefactTemplates.scopeSystem')}
        </span>
      )}
      {isCaseTemplate && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
          <Briefcase className="h-3 w-3" />
          {t('velociraptor.orgTemplates.scopeCase')}
        </span>
      )}
      {template.isPlatform && (
        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
          <Globe className="h-3 w-3" />
          {t('velociraptor.orgTemplates.scopePlatform', 'Platform')}
        </span>
      )}
      {hasSettings && (
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/50 dark:text-violet-300">
          <Settings className="h-3 w-3" />
          {t('velociraptor.orgTemplates.hasSettings')}
        </span>
      )}
    </>
  )
}

function CardMeta({ template }: { template: ArtefactTemplate }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mt-2 flex items-center gap-4 text-xs text-slate-400 dark:text-slate-500">
      <span>
        {t('collections.artefactTemplates.artefactCount', { count: template.artefactCount })}
      </span>
      {template.supportedPlatforms.length > 0 && (
        <span>{template.supportedPlatforms.join(', ')}</span>
      )}
      <span className="inline-flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {template.lastTestedAt
          ? t('velociraptor.orgTemplates.lastTested', {
              date: new Date(template.lastTestedAt).toLocaleDateString(),
            })
          : t('velociraptor.orgTemplates.neverTested')}
      </span>
    </div>
  )
}

function ReadOnlyActions({
  onTestBuild,
  onSaveAsOrg,
}: {
  onTestBuild?: (() => void) | undefined
  onSaveAsOrg?: (() => void) | undefined
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="ml-2 flex shrink-0 items-center gap-1">
      <button
        onClick={onTestBuild}
        className="rounded-lg p-2 text-slate-400 hover:bg-violet-50 hover:text-violet-600 dark:hover:bg-violet-950/50 dark:hover:text-violet-400"
        aria-label={t('velociraptor.orgTemplates.testBuild')}
        title={t('velociraptor.orgTemplates.testBuild')}
      >
        <Hammer className="h-4 w-4" />
      </button>
      <button
        onClick={onSaveAsOrg}
        className="dark:hover:bg-[#8a9bba]/20/50 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-[#4e5e82] hover:bg-[#4e5e82]/5 dark:text-[#8a9bba]"
        title={t('velociraptor.orgTemplates.saveAsOrgTemplate')}
      >
        <Copy className="h-3.5 w-3.5" />
        {t('velociraptor.orgTemplates.saveAsOrgTemplate')}
      </button>
    </div>
  )
}

function DeleteConfirmBar({
  templateName,
  onConfirm,
  onCancel,
}: {
  templateName: string
  onConfirm?: (() => void) | undefined
  onCancel?: (() => void) | undefined
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="ml-2 flex shrink-0 items-center gap-2">
      <span className="text-xs text-red-600 dark:text-red-400">
        {t('collections.artefactTemplates.deleteConfirmMessage', { name: templateName })}
      </span>
      <button
        onClick={onConfirm}
        className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
      >
        {t('collections.artefactTemplates.deleteTemplate')}
      </button>
      <button
        onClick={onCancel}
        className="rounded-lg border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        {t('collections.artefactTemplates.cancel')}
      </button>
    </div>
  )
}

function OrgActionButtons({
  onTestBuild,
  onCollectorSettings,
  onEdit,
  onDelete,
}: {
  onTestBuild?: (() => void) | undefined
  onCollectorSettings?: (() => void) | undefined
  onEdit?: (() => void) | undefined
  onDelete?: (() => void) | undefined
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="ml-2 flex shrink-0 items-center gap-1">
      <button
        onClick={onTestBuild}
        className="rounded-lg p-2 text-slate-400 hover:bg-violet-50 hover:text-violet-600 dark:hover:bg-violet-950/50 dark:hover:text-violet-400"
        aria-label={t('velociraptor.orgTemplates.testBuild')}
        title={t('velociraptor.orgTemplates.testBuild')}
      >
        <Hammer className="h-4 w-4" />
      </button>
      <button
        onClick={onCollectorSettings}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label={t('velociraptor.orgTemplates.collectorSettings')}
        title={t('velociraptor.orgTemplates.collectorSettings')}
      >
        <Settings className="h-4 w-4" />
      </button>
      <button
        onClick={onEdit}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label={t('collections.artefactTemplates.editTemplate')}
      >
        <Edit2 className="h-4 w-4" />
      </button>
      <button
        onClick={onDelete}
        className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400"
        aria-label={t('collections.artefactTemplates.deleteTemplate')}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}
